import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import routes from "../../shared/routes";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SideNavigationBar(props) {
  const { outsideClickRef, userType } = props;

  const location = useLocation();
  const getRoutes = routes(location.pathname, userType);
  const routesArray = getRoutes["routes"];

  let lastSection = null;

  return (
    <div
      id="main-sidebar"
      className="layout-sidebar hide-for-print rounded-2xl"
      ref={outsideClickRef}
      style={{
        background: "var(--nav-bg)",
        borderRight: "var(--nav-border)",
      }}
    >
      {/* Navigation Header */}
      <div className="flex grow flex-col gap-y-6 overflow-y-auto pb-4">
        {/* Brand Section */}
        <div
          className="flex h-20 shrink-0 items-center pl-4 pr-6"
          style={{ borderBottom: "var(--nav-border)" }}
        >
          <div className="flex items-center space-x-4">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ backgroundColor: "var(--nav-active-accent)" }}
            >
              <span className="text-xl font-bold text-white">F</span>
            </div>
            <div className="flex flex-col">
              <p
                className="text-lg font-semibold tracking-tight"
                style={{ color: "var(--nav-text)" }}
              >
                FluxOrders Admin
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex flex-1 flex-col layout-menu-container" aria-label="Main">
          <ul className="flex flex-1 flex-col space-y-1">
            {routesArray.map((item) => {
              const showSection =
                item.section && item.section !== lastSection;
              if (showSection) lastSection = item.section;

              return (
                <Fragment key={item.name}>
                  {showSection && (
                    <li
                      key={`section-${item.section}`}
                      className="nav-section-label"
                    >
                      {item.section}
                    </li>
                  )}
                  <li>
                  {!item.children ? (
                    <Link
                      to={item.href}
                      className={classNames(
                        item.current
                          ? "border-l-[3px] font-semibold"
                          : "border-l-[3px] border-transparent hover:bg-[var(--nav-hover-bg)] transition-all duration-200",
                        "flex items-center pl-4 pr-6 py-3 text-sm leading-6 relative focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nav-active-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--nav-bg)]"
                      )}
                      style={{
                        ...(item.current
                          ? {
                              backgroundColor: "var(--nav-active-bg)",
                              borderLeftColor: "var(--nav-active-accent)",
                              color: "var(--nav-active-accent)",
                            }
                          : { color: "var(--nav-text)" }),
                      }}
                    >
                      <item.icon
                        className="h-5 w-5 shrink-0"
                        style={{
                          color: item.current
                            ? "var(--nav-active-accent)"
                            : "var(--nav-text-secondary)",
                        }}
                        aria-hidden="true"
                      />
                      <span className="flex-1 truncate ml-4">{item.name}</span>
                      {item.current && (
                        <span className="sr-only"> (Current)</span>
                      )}
                    </Link>
                  ) : (
                    <Disclosure
                      as="div"
                      defaultOpen={item.open}
                      className="sidebar-parent-div"
                    >
                      {({ open }) => (
                        <>
                          <Disclosure.Button
                            className={classNames(
                              item.current
                                ? "border-l-[3px] font-semibold"
                                : "border-l-[3px] border-transparent hover:bg-[var(--nav-hover-bg)] transition-all duration-200",
                              "flex items-center w-full text-left pl-4 pr-6 py-3 text-sm leading-6 relative focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nav-active-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--nav-bg)]"
                            )}
                            style={{
                              ...(item.current
                                ? {
                                    backgroundColor: "var(--nav-active-bg)",
                                    borderLeftColor: "var(--nav-active-accent)",
                                    color: "var(--nav-active-accent)",
                                  }
                                : { color: "var(--nav-text)" }),
                            }}
                          >
                            <item.icon
                              className="h-5 w-5 shrink-0"
                              style={{
                                color: item.current
                                  ? "var(--nav-active-accent)"
                                  : "var(--nav-text-secondary)",
                              }}
                              aria-hidden="true"
                            />
                            <span className="flex-1 truncate text-left ml-4">
                              {item.name}
                            </span>
                            <div className="ml-2 flex items-center">
                              {open ? (
                                <ChevronDownIcon
                                  className="h-4 w-4 shrink-0 transition-transform duration-200"
                                  style={{ color: "var(--nav-text-secondary)" }}
                                  aria-hidden="true"
                                />
                              ) : (
                                <ChevronRightIcon
                                  className="h-4 w-4 shrink-0 transition-transform duration-200"
                                  style={{ color: "var(--nav-text-secondary)" }}
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                          </Disclosure.Button>
                          <Transition
                            enter="transition-all duration-300 ease-out"
                            enterFrom="opacity-0 max-h-0 transform scale-95"
                            enterTo="opacity-100 max-h-96 transform scale-100"
                            leave="transition-all duration-200 ease-in"
                            leaveFrom="opacity-100 max-h-96 transform scale-100"
                            leaveTo="opacity-0 max-h-0 transform scale-95"
                          >
                            <Disclosure.Panel as="ul" className="mt-1 space-y-1">
                              {item.children.map((subItem) => (
                                <li key={subItem.name}>
                                  <Link
                                    to={subItem.href}
                                    className={classNames(
                                      subItem.current
                                        ? "border-l-[3px] font-semibold"
                                        : "border-l-[3px] border-transparent hover:bg-[var(--nav-hover-bg)] transition-all duration-200",
                                      "block py-2.5 pl-8 pr-6 text-sm leading-6 relative focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nav-active-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--nav-bg)]"
                                    )}
                                    style={{
                                      ...(subItem.current
                                        ? {
                                            backgroundColor:
                                              "var(--nav-active-bg)",
                                            borderLeftColor:
                                              "var(--nav-active-accent)",
                                            color: "var(--nav-active-accent)",
                                          }
                                        : {
                                            color: "var(--nav-text-secondary)",
                                          }),
                                    }}
                                  >
                                    <span className="block truncate">
                                      {subItem.name}
                                    </span>
                                    {subItem.current && (
                                      <span className="sr-only">
                                        {" "}
                                        (Current)
                                      </span>
                                    )}
                                  </Link>
                                </li>
                              ))}
                            </Disclosure.Panel>
                          </Transition>
                        </>
                      )}
                    </Disclosure>
                  )}
                  </li>
                </Fragment>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
